<template>
  <div class="audit-log">
    <v-snackbar :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-clipboard-text-clock"
      title="Log Audit"
      subtitle="Rekod automatik setiap aktiviti pengguna (Create / Update / Delete) untuk kawalan dalaman, keselamatan dan pematuhan audit"
    />

    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-clipboard-text-search-outline</v-icon>
        <span class="subtitle-1 font-weight-medium">Carian Log Audit</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ total }} rekod</v-chip>
        <v-chip v-if="negeriTerhad" x-small color="grey lighten-2" class="ml-2 font-weight-medium grey--text text--darken-1">
          <v-icon x-small left>mdi-map-marker-outline</v-icon>Terhad ke {{ negeriTerhadLabel }} sahaja
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn color="green darken-2" dark class="text-none" :loading="exporting" :disabled="!total" @click="exportCsv">
          <v-icon left>mdi-file-delimited</v-icon>
          CSV
        </v-btn>
      </v-card-title>

      <!-- Bar penapis — susunan yang SAMA seperti Jadual Awalan dan Julat Harga:
           label kening di ATAS medan, bukan label terapung Vuetify di dalamnya.
           Dua sebab ia diseragamkan: label terapung beralih tempat apabila
           medan diisi (jadi lajur penapis "bergoyang" ketika digunakan), dan
           latar teal jalur ini menandakan "kawasan penapis" pada semua halaman.
           `persistent-placeholder` mengekalkan teks "Semua" kelihatan walaupun
           medan kosong dan tidak difokus — tanpanya medan kosong menjadi kotak
           tanpa petunjuk kerana label dalamannya sudah dibuang. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="6" sm="3" md="2">
            <div class="fld__label">Tarikh Dari</div>
            <v-text-field v-model="filters.dateFrom" type="date" outlined dense hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <div class="fld__label">Tarikh Hingga</div>
            <v-text-field v-model="filters.dateTo" type="date" outlined dense hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <div class="fld__label">Pengguna</div>
            <v-autocomplete
              v-model="filters.userID"
              :items="userItems"
              placeholder="Semua" persistent-placeholder
              outlined dense hide-details clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <div class="fld__label">Modul / Transaksi</div>
            <v-autocomplete
              v-model="filters.modul"
              :items="lookups.moduls"
              placeholder="Semua" persistent-placeholder
              outlined dense hide-details clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <div class="fld__label">Form / Jadual</div>
            <v-autocomplete
              v-model="filters.formName"
              :items="filteredFormNames"
              placeholder="Semua" persistent-placeholder
              outlined dense hide-details clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <div class="fld__label">Jenis Aktiviti</div>
            <v-select
              v-model="filters.activityType"
              :items="activityItems"
              placeholder="Semua" persistent-placeholder
              outlined dense hide-details clearable
            ></v-select>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <div class="fld__label">HID</div>
            <v-text-field v-model="filters.HID" placeholder="Semua" persistent-placeholder outlined dense hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <div class="fld__label">ID Rekod</div>
            <v-text-field v-model="filters.recordId" placeholder="Semua" persistent-placeholder outlined dense hide-details clearable></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto" class="d-flex align-center">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Results -->
      <v-data-table
        :headers="headers"
        :items="rows"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
        dense
        class="audit-table"
        @click:row="openDetail"
      >
        <template v-slot:item.tarikh="{ item }">
          <div class="text-no-wrap">{{ formatDate(item.tarikh) }}</div>
          <div class="text-no-wrap text--secondary sel-meta">{{ formatTime(item.tarikh) }}</div>
        </template>
        <template v-slot:item.user="{ item }">
          <div class="text-no-wrap">
            <strong>{{ item.userName || item.userID || '—' }}</strong>
            <span v-if="item.userID" class="sel-meta text--secondary"> (#{{ item.userID }})</span>
          </div>
          <div class="sel-meta text--secondary">{{ item.role || '' }}</div>
        </template>
        <template v-slot:item.activity_type="{ item }">
          <v-chip x-small dark :color="activityColor(item.activity_type)" class="font-weight-medium">
            {{ item.activity_type }}
          </v-chip>
          <div v-if="item.url" class="url-cell mt-1" :title="item.url">{{ item.method }} {{ item.url }}</div>
        </template>
        <template v-slot:item.form_name="{ item }">
          <span v-if="item.form_name" class="form-cell">{{ item.form_name }}</span>
          <span v-else class="text--disabled">—</span>
        </template>
        <template v-slot:item.HID="{ item }">
          <span v-if="item.HID">{{ item.HID }}</span>
          <span v-else class="text--disabled">—</span>
        </template>
        <template v-slot:item.record_id="{ item }">
          <span v-if="item.record_id">{{ item.record_id }}</span>
          <span v-else class="text--disabled">—</span>
        </template>
        <template v-slot:item.diff="{ item }">
          <v-icon small :color="item.hasBefore || item.hasAfter ? 'primary' : 'grey lighten-1'">
            {{ item.hasBefore || item.hasAfter ? 'mdi-file-compare' : 'mdi-minus' }}
          </v-icon>
        </template>
        <template v-slot:no-data>
          <div class="py-8 text-center grey--text">Tiada rekod log bagi penapis semasa</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Detail: before / after values -->
    <v-dialog v-model="detailDialog" max-width="900px" scrollable>
      <v-card v-if="detail">
        <v-card-title class="py-3">
          <v-icon small class="mr-2">mdi-file-compare</v-icon>
          <span class="subtitle-1 font-weight-medium">Butiran Log #{{ detail.id }}</span>
          <v-spacer></v-spacer>
          <v-chip x-small dark :color="activityColor(detail.activity_type)" class="font-weight-medium mr-2">
            {{ detail.activity_type }}
          </v-chip>
          <v-btn icon small @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense class="mb-2">
            <v-col v-for="m in detailMeta" :key="m.label" cols="6" sm="4" md="3">
              <div class="fld__label">{{ m.label }}</div>
              <div class="body-2 font-weight-medium meta-val">{{ m.value }}</div>
            </v-col>
          </v-row>

          <template v-if="diffRows.length">
            <div class="subtitle-2 mb-2">Nilai Sebelum &amp; Selepas</div>
            <v-simple-table dense class="diff-table">
              <thead>
                <tr>
                  <th style="width:28%">Medan</th>
                  <th style="width:36%">Sebelum</th>
                  <th style="width:36%">Selepas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in diffRows" :key="r.key" :class="{ changed: r.changed }">
                  <td class="font-weight-medium">{{ r.key }}</td>
                  <td :class="{ 'before-cell': r.changed }">{{ r.before }}</td>
                  <td :class="{ 'after-cell': r.changed }">{{ r.after }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="sel-meta text--secondary mt-2">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              Baris berlorek menunjukkan medan yang berubah.
            </div>
          </template>
          <template v-else-if="rawBefore || rawAfter">
            <v-row dense>
              <v-col cols="12" md="6" v-if="rawBefore">
                <div class="subtitle-2 mb-1">Nilai Sebelum</div>
                <pre class="raw-json">{{ rawBefore }}</pre>
              </v-col>
              <v-col cols="12" md="6" v-if="rawAfter">
                <div class="subtitle-2 mb-1">Nilai Selepas</div>
                <pre class="raw-json">{{ rawAfter }}</pre>
              </v-col>
            </v-row>
          </template>
          <div v-else class="text-center grey--text py-6">Tiada data nilai sebelum/selepas bagi rekod ini</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AuditLogServices from "@/services/auditLog";
import { negeriStore, loadNegeriList } from "@/services/negeriList";
import { negeriLock } from "@/services/negeriScope";

const svc = new AuditLogServices();

const ACTIVITY_COLORS = {
  CREATE: "green darken-1",
  UPDATE: "amber darken-2",
  DELETE: "red darken-1",
  DOWNLOAD: "teal darken-1",
  UPLOAD: "deep-purple darken-1",
  GENERATE: "indigo darken-1",
  LOGIN: "blue darken-1",
  LOGOUT: "blue-grey",
};

const EMPTY_FILTERS = () => ({
  dateFrom: "", dateTo: "", userID: null, modul: null, formName: null,
  activityType: null, HID: "", recordId: "",
});

export default {
  name: "AuditLog",
  data() {
    return {
      snackbar: { show: false, message: "", color: "success", timeout: 3000 },
      filters: EMPTY_FILTERS(),
      lookups: { moduls: [], activities: [], users: [], formNames: [], modulForms: {} },
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      options: { page: 1, itemsPerPage: 50 },
      detailDialog: false,
      detail: null,
      headers: [
        { text: "Tarikh/Masa", value: "tarikh", sortable: false, width: 160 },
        { text: "Pengguna", value: "user", sortable: false },
        { text: "Modul", value: "modul", sortable: false },
        { text: "Form / Jadual", value: "form_name", sortable: false },
        { text: "Aktiviti", value: "activity_type", sortable: false, width: 220 },
        { text: "HID", value: "HID", sortable: false },
        { text: "ID Rekod", value: "record_id", sortable: false, width: 90 },
        { text: "Nilai", value: "diff", sortable: false, width: 60, align: "center" },
      ],
    };
  },
  computed: {
    // PP/PO hanya nampak rekod negeri sendiri (backend scope NG = kodneg).
    // Papar badge amaran supaya pengguna tahu senarai ini tidak menyeluruh.
    negeriTerhad() {
      return negeriLock(this.$store, "auditLog").locked;
    },
    // Nama negeri kunci pengguna untuk teks badge, cth. "Selangor".
    // Jatuh balik ke kod mentah jika lookup belum dimuatkan / tidak dikenali.
    negeriTerhadNama() {
      const kodneg = negeriLock(this.$store, "auditLog").kodneg;
      if (!kodneg) return "";
      const raw = String(kodneg).trim();
      return this.negeriNameByCode[raw] || this.negeriNameByCode[raw.padStart(2, "0")] || raw;
    },
    // "Negeri Selangor" — tapi elak "Negeri Negeri Sembilan": jangan tambah
    // awalan "Negeri" jika nama negeri itu sendiri sudah bermula dengan "Negeri".
    negeriTerhadLabel() {
      const nama = this.negeriTerhadNama;
      if (!nama) return "";
      return /^negeri\b/i.test(nama) ? nama : `Negeri ${nama}`;
    },
    userItems() {
      return this.lookups.users.map(u => ({
        text: u.userName ? `${u.userName} (#${u.userID})` : `#${u.userID}`,
        value: u.userID,
      }));
    },
    filteredFormNames() {
      if (this.filters.modul && this.lookups.modulForms[this.filters.modul]) {
        return this.lookups.modulForms[this.filters.modul];
      }
      return this.lookups.formNames || [];
    },
    activityItems() {
      // union of the standard CUD set and whatever exists in the data
      const std = ["CREATE", "UPDATE", "DELETE", "DOWNLOAD", "UPLOAD", "GENERATE", "LOGIN", "LOGOUT"];
      return [...new Set([...std, ...this.lookups.activities])];
    },
    // filter payload sent to the API (page info added per call)
    apiFilters() {
      const f = this.filters;
      const out = {};
      ["dateFrom", "dateTo", "userID", "modul", "formName", "activityType", "HID", "recordId"].forEach(k => {
        if (f[k] !== null && f[k] !== undefined && String(f[k]).trim() !== "") out[k] = f[k];
      });
      return out;
    },
    negeriNameByCode() {
      const map = {};
      (negeriStore.list || []).forEach(n => { map[String(n.id)] = n.nama; });
      return map;
    },
    detailMeta() {
      const d = this.detail;
      if (!d) return [];
      return [
        { label: "Tarikh/Masa", value: this.dt(d.tarikh) },
        { label: "Pengguna", value: (d.userName || "—") + (d.userID ? ` (#${d.userID})` : "") },
        { label: "Peranan", value: d.role || "—" },
        { label: "Negeri", value: this.negeriLabel(d.NG) },
        { label: "Modul", value: d.modul || "—" },
        { label: "Form_Name", value: d.form_name || "—" },
        { label: "HID", value: d.HID || "—" },
        { label: "ID Rekod", value: d.record_id || "—" },
        { label: "URL", value: d.url || "—" },
        { label: "Kaedah", value: d.method || "—" },
        { label: "IP", value: d.ip || "—" },
        { label: "Status", value: d.status_code || "—" },
      ];
    },
    // before/after as aligned field rows when both parse as objects
    diffRows() {
      const d = this.detail;
      if (!d) return [];
      const b = d.data_before, a = d.data_after;
      const isObj = v => v && typeof v === "object" && !Array.isArray(v);
      if (!isObj(b) && !isObj(a)) return [];
      const keys = [...new Set([...Object.keys(isObj(b) ? b : {}), ...Object.keys(isObj(a) ? a : {})])];
      const show = v => (v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
      return keys.map(k => {
        const bv = isObj(b) ? b[k] : undefined;
        const av = isObj(a) ? a[k] : undefined;
        return {
          key: k,
          before: show(bv),
          after: show(av),
          changed: JSON.stringify(bv) !== JSON.stringify(av),
        };
      });
    },
    rawBefore() { return this.rawJson(this.detail && this.detail.data_before); },
    rawAfter() { return this.rawJson(this.detail && this.detail.data_after); },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        clearTimeout(this._filterTimer);
        this._filterTimer = setTimeout(() => this.searchNow(), 400);
      },
    },
    'filters.modul'(v) {
      if (this.filters.formName && !this.filteredFormNames.includes(this.filters.formName)) {
        this.filters.formName = null;
      }
    },
    // server-side pagination: refetch when the table page / page-size changes
    options: {
      deep: true,
      handler() { this.fetchRows(); },
    },
  },
  async created() {
    // Tidak di-await: kegagalan/kelewatannya tidak boleh menghalang log
    // dimuatkan. Sehingga ia selesai, negeriStore mengembalikan senarai
    // fallback, dan computed akan dikira semula apabila nama sebenar tiba.
    loadNegeriList();
    try {
      this.lookups = await svc.lookups();
    } catch (e) {
      // 403 → no access for this role. Don't fall through to fetchRows(): it would
      // 403 too and replace this message with the generic "Gagal memuatkan" one.
      const msg = (e.response && e.response.data && e.response.data.message) || "Tiada kebenaran akses log audit.";
      this.snackbar = { show: true, message: msg, color: "error", timeout: 5000 };
      if (e.response && e.response.status === 403) return;
    }
    this.fetchRows();
  },
  methods: {
    // Tarikh + masa untuk dialog butiran. Ia MENGGUNAKAN SEMULA dua pemformat
    // jadual di bawah dan bukan memformat sendiri: dahulu ia mengeluarkan
    // 2026-07-01 18:16:44 (ISO) sedangkan jadual di belakang dialog yang sama
    // menunjukkan 01/07/2026 — dua susunan tarikh untuk satu nilai, dan
    // 01/07 lwn 2026-07-01 mudah tersalah baca sebagai hari lwn bulan.
    dt(v) {
      if (!v) return "—";
      return `${this.formatDate(v)} ${this.formatTime(v)}`.trim();
    },
    // Date-only, DD/MM/YYYY (e.g. 01/07/2026)
    formatDate(t) {
      if (!t) return "—";
      const d = new Date(t);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    },
    // Time-only, HH:MM:SS (e.g. 18:16:44)
    formatTime(t) {
      if (!t) return "";
      return new Date(t).toTimeString().slice(0, 8);
    },
    // Log menyimpan KOD negeri sahaja (cth. "10"). Kod sendirian tidak
    // bermakna kepada pembaca, jadi dipaparkan sebagai "Selangor (10)".
    // Padding: rekod lama boleh menyimpan "1" sedangkan lookup memakai "01".
    // Jika kod tidak dikenali, kod mentah tetap ditunjukkan — lebih baik
    // daripada "—" yang menyembunyikan fakta bahawa nilai itu wujud.
    negeriLabel(code) {
      if (code === null || code === undefined || String(code).trim() === "") return "—";
      const raw = String(code).trim();
      const nama = this.negeriNameByCode[raw] || this.negeriNameByCode[raw.padStart(2, "0")];
      return nama ? `${nama} (${raw})` : raw;
    },
    activityColor(t) { return ACTIVITY_COLORS[t] || "grey"; },
    rawJson(v) {
      if (v == null) return "";
      return typeof v === "string" ? v : JSON.stringify(v, null, 2);
    },
    // Filesystem-safe filename fragment: non-alphanumerics → '-', capped.
    slug(s) {
      return String(s == null ? "" : s)
        .trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "x";
    },
    // compact "YYYYMMDD-HHMM" stamp for filenames
    makeStamp() {
      const d = new Date();
      const p = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
    },
    // Export filename encodes the active filters (like the Jadual Awalan / Julat
    // Harga pages) so two exports never collide in the Downloads folder. Only the
    // filters actually set are appended. A method, not a computed — the timestamp
    // must be re-read on each click rather than cached until a filter changes.
    exportFileName() {
      const f = this.filters;
      const parts = ["Log_Audit"];
      if (f.modul) parts.push("modul-" + this.slug(f.modul));
      if (f.formName) parts.push("form-" + this.slug(f.formName));
      if (f.activityType) parts.push(this.slug(f.activityType));
      if (f.userID) {
        const u = this.lookups.users.find(x => x.userID === f.userID);
        parts.push("pengguna-" + this.slug((u && u.userName) || f.userID));
      }
      if (f.HID) parts.push("hid-" + this.slug(f.HID));
      if (f.recordId) parts.push("rekod-" + this.slug(f.recordId));
      if (f.dateFrom || f.dateTo) {
        parts.push((f.dateFrom || "awal") + "_hingga_" + (f.dateTo || "kini"));
      }
      parts.push(this.makeStamp());
      return parts.join("_") + ".csv";
    },
    searchNow() {
      // new search → back to page 1 (options watcher triggers the fetch;
      // if already on page 1, fetch directly)
      if (this.options.page !== 1) this.options.page = 1;
      else this.fetchRows();
    },
    resetFilters() {
      this.filters = EMPTY_FILTERS();
      this.searchNow();
    },
    async fetchRows() {
      this.loading = true;
      try {
        const d = await svc.search({
          ...this.apiFilters,
          page: this.options.page,
          perPage: this.options.itemsPerPage,
        });
        this.rows = d.rows || [];
        this.total = d.total || 0;
      } catch (e) {
        this.rows = [];
        this.total = 0;
        const msg = (e.response && e.response.data && e.response.data.message) || "Gagal memuatkan log audit.";
        this.snackbar = { show: true, message: msg, color: "error", timeout: 4000 };
      }
      this.loading = false;
    },
    async openDetail(row) {
      try {
        this.detail = await svc.detail(row.id);
        this.detailDialog = true;
      } catch (e) {
        this.snackbar = { show: true, message: "Gagal memuatkan butiran log.", color: "error", timeout: 4000 };
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const blob = await svc.exportCsv(this.apiFilters);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.exportFileName();
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.snackbar = { show: true, message: "Eksport CSV gagal.", color: "error", timeout: 4000 };
      }
      this.exporting = false;
    },
  },
};
</script>

<style scoped>
.audit-log { padding: 16px; }

/* ---------- Bar penapis ----------
   Disalin daripada Jadual Awalan / Julat Harga dengan sengaja: jalur teal
   pucat + birai atas/bawah ialah isyarat "kawasan penapis" di seluruh sistem.
   Halaman ini dahulunya memakai v-card-text kosong dengan label terapung, jadi
   ia satu-satunya halaman yang penapisnya kelihatan seperti borang biasa. */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}
/* Label kening — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar ::v-deep .v-input fieldset {
  border-color: #cfeae7;
}
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-select__selection--comma {
  color: #21403c;
}
.filter-bar ::v-deep .v-input__append-inner .v-icon {
  color: #7aa8a2;
}
/* Teal, bukan `color="primary"`: primary Vuetify ialah biru dan bertentangan
   dengan sistem teal — masalah yang sama sudah diperbaiki pada halaman lain. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

::v-deep .audit-table tbody tr { cursor: pointer; }
.form-cell { display: inline-block; max-width: 340px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
/* `display: block`, BUKAN inline-block: elemen ini ialah <div> selepas cip
   aktiviti, jadi ia sepatutnya jatuh ke baris sendiri — tetapi inline-block
   menariknya kembali ke sisi cip, dan URL panjang menolak lebar lajur.
   `max-width: 100%` (bukan 320px tetap) mengehadkannya kepada lebar lajur
   sebenar supaya ellipsis berlaku dalam sel, bukan melimpahinya. */
.url-cell { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--fs-xs); color: #64748B; }
.meta-val { word-break: break-all; }
/* Baris kedua dalam sel jadual (masa, peranan, #ID) dan nota kaki dialog.
   Ia menyokong nilai utama di atasnya, jadi ia lebih kecil — tetapi satu takuk
   sahaja (13px), bukan 12px `caption`, supaya masih senang dibaca sebaris
   dengan data 14px. */
.sel-meta { font-size: var(--fs-xs); line-height: 1.4; }

/* before/after diff: shade only the changed rows */
.diff-table td { font-size: var(--fs-sm); word-break: break-all; }
.diff-table tr.changed td { background: #FFFDE7; }
.diff-table td.before-cell { background: #FFEBEE; color: #B71C1C; }
.diff-table td.after-cell { background: #E8F5E9; color: #1B5E20; }

.raw-json {
  background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 6px;
  padding: 10px; font-size: var(--fs-xs); max-height: 320px; overflow: auto; white-space: pre-wrap;
}
</style>
